// Seed question library for the pre-session intake.
// In production this lives in the `margin_questions` table and is editable via
// the admin view in the Margin spoke (PAR-side). This file is the v1 seed.
//
// Field types: text | textarea | email | tel | number | select | multiselect | calendar
// Section ids match the spec.

export const SECTIONS = [
  {
    id: 'identity',
    title: 'About you',
    intro: 'I just need a way to reach you.',
  },
  {
    id: 'business',
    title: 'About the business',
    intro: "Tell me the basics. We'll dig into the rest together.",
  },
  {
    id: 'reality',
    title: 'The reality',
    intro: "Tell me how it actually feels to run this. Not the version you tell people. There are no wrong answers here.",
  },
  {
    id: 'weight',
    title: 'The weight',
    intro: "Most owners are carrying things they haven't said out loud. This is the place to say it.",
  },
  {
    id: 'tools',
    title: 'The tools',
    intro: "I'm not going to ask you to switch tools. I just want to see the picture.",
  },
  {
    id: 'better',
    title: "What 'better' looks like",
    intro: "This is the part most owners don't get to think about.",
  },
  {
    id: 'session',
    title: 'Book the session',
    intro: "Ninety minutes, working session, not a sales call. You'll leave with a clearer picture of your own business whether or not we go further.",
  },
];

export const QUESTIONS = [
  // Identity
  { id: 'first_name', section: 'identity', order: 1, prompt: 'First name', type: 'text', required: true },
  { id: 'last_name', section: 'identity', order: 2, prompt: 'Last name', type: 'text', required: true },
  { id: 'email', section: 'identity', order: 3, prompt: 'Email', type: 'email', required: true },
  { id: 'phone', section: 'identity', order: 4, prompt: 'Phone', type: 'tel', required: false },
  { id: 'source', section: 'identity', order: 5, prompt: 'How did you hear about Margin?', type: 'text', required: false },

  // Business
  { id: 'business_name', section: 'business', order: 1, prompt: 'Business name', type: 'text', required: true },
  { id: 'business_description', section: 'business', order: 2, prompt: 'What does the business do?', helper: 'One or two sentences is fine.', type: 'textarea', required: true, rows: 3 },
  {
    id: 'business_age', section: 'business', order: 3, prompt: 'How long has it been running?', type: 'select', required: true,
    options: ['Less than a year', '1–3 years', '3–7 years', '7–15 years', '15+ years']
  },
  { id: 'team_size', section: 'business', order: 4, prompt: 'How many people on the team, including you?', type: 'number', required: true, min: 1 },
  {
    id: 'operating_modes', section: 'business', order: 5, prompt: 'How does the business run?', helper: 'Choose any that apply.', type: 'multiselect', required: true,
    options: ['Single location', 'Multi-location', 'Online only', 'Mobile / in-home', 'Hybrid']
  },
  {
    id: 'business_type', section: 'business', order: 6, prompt: 'Business type', type: 'select', required: true,
    options: [
      'Franchise / Enrichment',
      'Retail',
      'Food & Beverage',
      'Professional Services',
      'Health & Wellness',
      'Trades & Home Services',
      'Nonprofit / Mission-driven',
      'Other',
    ]
  },
  {
    id: 'modifier_flags', section: 'business', order: 7, prompt: 'Anything else true about right now?', helper: 'Optional. Choose any that apply.', type: 'multiselect', required: false,
    options: [
      'Just made our first hire, still figuring out the transition',
      'Owner is still the primary practitioner (still teaching/cutting/treating/etc.)',
      'Multi-location or scaling',
      'Seasonal business',
      'Franchise model with corporate compliance',
    ]
  },

  // Reality
  {
    id: 'success_vision', section: 'reality', order: 1,
    prompt: 'What does a successful year look like for you?',
    helper: 'In your words: revenue, time off, full classes, less stress, whatever success actually means to you.',
    type: 'textarea', required: true, rows: 5,
  },
  {
    id: 'money_vs_time', section: 'reality', order: 2,
    prompt: 'What parts of the business actually make money, and what just takes time?',
    type: 'textarea', required: false, rows: 4,
  },
  {
    id: 'hate_most', section: 'reality', order: 3,
    prompt: 'What do you hate doing the most?',
    type: 'textarea', required: true, rows: 3,
  },
  {
    id: 'falling_through', section: 'reality', order: 4,
    prompt: "What's constantly falling through the cracks?",
    type: 'textarea', required: true, rows: 4,
  },
  {
    id: 'eliminate_three', section: 'reality', order: 5,
    prompt: 'If you could eliminate three tasks tomorrow, what would they be?',
    type: 'textarea', required: true, rows: 4,
  },

  // Weight
  {
    id: 'carrying_alone', section: 'weight', order: 1,
    prompt: "What's the thing you've been carrying alone?",
    type: 'textarea', required: false, rows: 6,
  },
  {
    id: 'in_the_dark', section: 'weight', order: 2,
    prompt: 'Where do you feel "in the dark": financially, operationally, anywhere?',
    type: 'textarea', required: false, rows: 4,
  },

  // Tools
  {
    id: 'tools_list', section: 'tools', order: 1,
    prompt: 'List the main tools you use to run the business.',
    helper: 'Name + monthly cost if you know it. One per line.',
    type: 'textarea', required: true, rows: 8,
  },
  {
    id: 'duplicate_entry', section: 'tools', order: 2,
    prompt: 'Where are you entering the same information in more than one place?',
    type: 'textarea', required: false, rows: 3,
  },

  // Better
  {
    id: 'hours_back', section: 'better', order: 1,
    prompt: 'If you got 5–10 hours back every week, what would you do with them?',
    type: 'textarea', required: true, rows: 4,
  },
  {
    id: 'calm_vs_reactive', section: 'better', order: 2,
    prompt: 'What would it feel like for the business to run calm instead of reactive?',
    type: 'textarea', required: false, rows: 4,
  },
  {
    id: 'scale_without_burnout', section: 'better', order: 3,
    prompt: 'What would let you scale without burning out?',
    type: 'textarea', required: false, rows: 4,
  },

  // Session
  {
    id: 'session_slot', section: 'session', order: 1,
    prompt: 'Pick a time',
    helper: '90 minutes. Times shown in your timezone.',
    type: 'calendar', required: true,
  },
  {
    id: 'pre_session_notes', section: 'session', order: 2,
    prompt: 'Anything I should know before we sit down?',
    type: 'textarea', required: false, rows: 4,
  },
];
